import React, { useState, useRef } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { publicRequest } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { GoogleReCaptcha, googleReCaptchaValidation } from './GoogleReCaptcha'
import CustomTextField from './_form_fields/CustomTextField'
import CustomBackdrop from './CustomBackdrop'

const PasswordResetWrapper = (props) => {
  const { setLoginDialogOpen, setPasswordResetDialogOpen, handleNotificationDialogOpen } = props
  const theme = useTheme()

  const [email, setEmail] = useState('')
  const [captchaValidated, setCaptchaValidated] = useState(false)
  const [errorCaptcha, setErrorCaptcha] = useState(false)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const captchaRef = useRef('')

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const formControlStyle = {
    background: theme.palette.azureishWhite,
    color: theme.palette.violetsAreBlue,
    borderRadius: '30px',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
  }

  const outlinedInputErrorStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
  }

  const loginErrorHelperStyle = {
    color: theme.palette.congoPink,
    paddingLeft: '20px',
  }

  const loginButtonStyle = {
    textTransform: 'capitalize',
    fontWeight: '400',
    color: theme.palette.aliceBlue,
    fontSize: '18px',
    textDecoration: 'underline',

    '&:hover': {
      background: 'inherit',
      textDecoration: 'none',
    },
  }

  const submitButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.violetsAreBlue,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.violetsAreBlue, 0.8),
    },
  }

  const handleChangeEmail = (e) => {
    const value = e.target.value
    setEmail(value)
  }

  const scrollToTop = () => {
    const element = document.getElementById('formStart')
    element.scrollIntoView({ behavior: 'smooth' })
  }

  const handleClickLogin = () => {
    setPasswordResetDialogOpen(false)
    setLoginDialogOpen(true)
  }

  const passwordresetRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsersPasswordReset
      
    const request_data = { url, method: 'PUT', data: postData }
    
    const response = await publicRequest(request_data)
    
    if (response && response.status_code && response.status_code < 400 && response.data && response.data.status && response.data.status === 'reset_sent') {
      setPasswordResetDialogOpen(false)
      handleNotificationDialogOpen({
        title: defaultTexts.resetPassword,
        text: defaultTexts.resetPasswordSuccessfullText,
      })
    } else if (response && response.error && response.error.includes('User does not exist.')) {
      scrollToTop()
      setError(defaultTexts.userNotExists)
    } else {
      scrollToTop()
      setError(defaultTexts.anErrorOccured)
    }
  }

  const handleClickSubmit = async () => {
    setIsLoading(true)
    setError(null)

    const captchaResponse = captchaValidated ? true : await googleReCaptchaValidation(captchaRef.current.getValue())
    setCaptchaValidated(captchaResponse ? true : false)

    if (email && captchaResponse) {
      passwordresetRequest({ email })
    }
    
    if (!email) {
      scrollToTop()
      setError(defaultTexts.pleaseFillOutEmail)
    }
    
    if (!captchaResponse) {
      setErrorCaptcha(true)
    }

    setIsLoading(false)
  }

  return (
    <Box id='formStart'>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      {
        error &&
        <Alert severity='error' sx={{ fontSize: '16px' }}>{error}</Alert>
      }

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='filled'
          label={defaultTexts.email}
          currentValue={email}
          setCurrentValue={handleChangeEmail}
          required={true}
          loginError={error ? true : false}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={{ padding: '12px 0px 32px 0px' }}>
        <center>
          <GoogleReCaptcha
            ref={captchaRef}
            error={errorCaptcha}
            errorColor={theme.palette.congoPink}
            errorCenter={true}
          />
        </center>
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems='center'
        justifyContent={{ xs: 'flex-start', md: 'space-between' }}
        spacing={1}
        sx={{ marginTop: '24px' }}
      >
        <Box>
          <Button
            variant='text'
            disableRipple
            disableElevation
            sx={loginButtonStyle}
            onClick={handleClickLogin}
          >
            {defaultTexts.login}
          </Button>
        </Box>
        <Box>
          <Button
            variant='text'
            disableRipple
            disableElevation
            sx={submitButtonStyle}
            onClick={handleClickSubmit}
          >
            {defaultTexts.send}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}

export default PasswordResetWrapper